"use client";

import { useState } from "react";

import { motion } from "framer-motion";
import { Check, ChevronRight } from "lucide-react";

import { CalBookingButton } from "@/components/ui/cal-booking-button";
import { cn } from "@/lib/utils";

import type { BillingPeriod, SubscriptionAddon, SubscriptionPlan } from "@/lib/constants/pricing";

interface PricingHeroSectionProps {
  plans: SubscriptionPlan[];
  voiceAgent: SubscriptionAddon;
}

// ---------------------------------------------------------------------------
// sub-components
// ---------------------------------------------------------------------------

function BillingToggle({ period, onChange }: { period: BillingPeriod; onChange: (p: BillingPeriod) => void }) {
  const isYearly = period === "yearly";

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={cn("font-semibold", !isYearly ? "text-ash-900" : "text-ash-500")}>Monthly</span>
      <button
        type="button"
        onClick={() => onChange(isYearly ? "monthly" : "yearly")}
        className={cn(
          "relative inline-flex h-4 w-7 shrink-0 cursor-pointer items-center rounded-full transition-colors",
          isYearly ? "bg-rebolt-500" : "bg-ash-300"
        )}
        aria-label="Toggle billing period"
      >
        <motion.span
          layout
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className="block h-3 w-3 rounded-full bg-white shadow-sm"
          style={{ marginLeft: isYearly ? "auto" : 2, marginRight: isYearly ? 2 : "auto" }}
        />
      </button>
      <span className={cn("font-semibold", isYearly ? "text-ash-900" : "text-ash-500")}>Yearly</span>
      {isYearly && <span className="text-xs font-semibold text-rebolt-500">(Get 2 months free)</span>}
    </div>
  );
}

function PriceDisplay({
  yearlyPrice,
  monthlyPrice,
  period,
  isDark,
  startingAt,
}: {
  yearlyPrice: number;
  monthlyPrice: number;
  period: BillingPeriod;
  isDark: boolean;
  startingAt?: boolean;
}) {
  const activePrice = period === "yearly" ? yearlyPrice : monthlyPrice;
  const inactivePrice = period === "yearly" ? monthlyPrice : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-0.75">
        <span
          className={cn("text-xl font-bold leading-none self-end pb-0.75", isDark ? "text-clay-300" : "text-ash-600")}
        >
          $
        </span>
        <span
          className={cn(
            "text-5xl font-extrabold leading-none tracking-[-0.75px]",
            isDark ? "text-white" : "text-ash-900"
          )}
        >
          {activePrice}
        </span>
        <div className="flex flex-col justify-end pt-1.5">
          {startingAt && (
            <span className={cn("text-[10px] font-semibold", isDark ? "text-ash-300" : "text-clay-400")}>
              Starting at
            </span>
          )}
          <span className={cn("text-sm font-extrabold", isDark ? "text-clay-400" : "text-ash-600")}>per month</span>
          {period === "yearly" && (
            <span className={cn("text-[10px] font-semibold", isDark ? "text-ash-300" : "text-clay-400")}>
              billed yearly
            </span>
          )}
        </div>
      </div>

      {inactivePrice && (
        <div className="flex items-start gap-0.75 opacity-60">
          <span
            className={cn("text-lg font-bold leading-none self-end pb-0.5", isDark ? "text-clay-400" : "text-ash-500")}
          >
            $
          </span>
          <span
            className={cn(
              "text-4xl font-extrabold leading-none tracking-[-0.5px] line-through decoration-1",
              isDark ? "text-clay-400" : "text-ash-500"
            )}
          >
            {inactivePrice}
          </span>
          <div className="flex flex-col justify-end pt-1">
            <span className={cn("text-sm font-extrabold", isDark ? "text-clay-500" : "text-ash-500")}>per month</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PricingCard({ plan, period }: { plan: SubscriptionPlan; period: BillingPeriod }) {
  const isDark = !!plan.popular;
  const { pricingPage } = plan;

  return (
    <div
      className={cn(
        "flex flex-col justify-between rounded-3xl border p-8",
        isDark
          ? "bg-ash-900 border-ash-100 md:flex-1 shadow-md w-full lg:w-[360px] md:min-h-[680px]"
          : "bg-clay-100 border-ash-100 w-full lg:w-[300px] md:min-h-[620px]"
      )}
    >
      <div className="flex flex-col gap-4">
        <span
          className={cn("text-xs font-bold uppercase tracking-[1.1px]", isDark ? "text-rebolt-500" : "text-clay-400")}
        >
          {pricingPage.eyebrow}
        </span>

        <h3 className={cn("text-2xl font-extrabold tracking-[-0.5px]", isDark ? "text-white" : "text-ash-900")}>
          {pricingPage.name}
        </h3>

        <p className={cn("text-sm leading-[1.4]", isDark ? "text-clay-300" : "text-clay-600")}>
          {pricingPage.description}
        </p>

        <PriceDisplay
          yearlyPrice={plan.pricing.yearly}
          monthlyPrice={plan.pricing.monthly}
          period={period}
          isDark={isDark}
        />

        <div className={cn("h-px w-full", isDark ? "bg-ash-700" : "bg-ash-200")} />

        <ul className="flex flex-col gap-2.5">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5">
              <Check
                className={cn("w-5 h-5 shrink-0 mt-0.5", isDark ? "text-rebolt-500" : "text-rebolt-500")}
                strokeWidth={2.5}
              />
              <span className={cn("text-sm leading-snug", isDark ? "text-clay-200" : "text-ash-700")}>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {isDark ? (
        <CalBookingButton
          variant="rebolt-primary"
          size="xl"
          className="mt-8 w-full rounded-full inline-flex items-center justify-center gap-2"
        >
          {pricingPage.cta}
          <ChevronRight className="w-5 h-5 shrink-0" />
        </CalBookingButton>
      ) : (
        <CalBookingButton variant="rebolt-outline" size="xl" className="mt-8 w-full rounded-full">
          {pricingPage.cta}
        </CalBookingButton>
      )}
    </div>
  );
}

function VoiceAgentCard({ addon }: { addon: SubscriptionAddon }) {
  return (
    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 rounded-3xl border border-ash-100 bg-clay-100 p-8 w-full max-w-5xl">
      <div className="flex flex-col gap-2 lg:max-w-[600px]">
        <span className="text-xs font-bold uppercase tracking-[1.1px] text-clay-400">{addon.eyebrow}</span>
        <h3 className="text-2xl font-extrabold tracking-[-0.5px] text-ash-900">{addon.name}</h3>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-extrabold tracking-[-0.5px] text-ash-900">
            <span className="text-sm font-semibold text-ash-500">Starting at </span>${addon.pricing.monthly}
            <span className="text-sm font-extrabold text-ash-600">/mo</span>
          </p>
          <p className="text-xs text-clay-500">Add to any plan. No contracts. Cancel anytime.</p>
        </div>

        <CalBookingButton variant="rebolt-outline" size="xl" className="rounded-full whitespace-nowrap">
          {addon.cta}
        </CalBookingButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// main export
// ---------------------------------------------------------------------------

export default function PricingHeroSection({ plans, voiceAgent }: PricingHeroSectionProps) {
  const [period, setPeriod] = useState<BillingPeriod>("yearly");

  return (
    <section className="w-full bg-white px-5 pt-36 md:pt-44 pb-16 md:pb-28">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 md:gap-12">
        {/* header */}
        <div className="flex flex-col items-center gap-3 md:gap-5 text-center">
          <span className="text-xs font-bold uppercase tracking-[1px] text-rebolt-500">Pricing</span>

          <h1 className="font-clash text-4xl lg:text-6xl font-medium leading-[1.05] tracking-[-1.2px] text-ash-900">
            Replace your marketing agency and CRM.
          </h1>

          <p className="text-lg leading-[1.4] text-ash-600 max-w-lg">
            Get more leads. Run your jobs. Manage everything in one platform.
          </p>
        </div>

        {/* billing toggle */}
        <BillingToggle period={period} onChange={setPeriod} />

        {/* pricing cards */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-end justify-center gap-5 w-full max-w-5xl">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={cn(
                plan.id === "marketing-kit" && "order-2 lg:order-1",
                plan.id === "mk-crm-bundle" && "order-1 lg:order-2",
                plan.id === "crm" && "order-3 lg:order-3"
              )}
            >
              <PricingCard plan={plan} period={period} />
            </div>
          ))}
        </div>

        {/* voice agent add-on */}
        <VoiceAgentCard addon={voiceAgent} />

        {/* footer note */}
        <p className="text-sm text-clay-500">No contracts. Cancel anytime.</p>
      </div>
    </section>
  );
}
